import React, { useEffect, useMemo, useState } from 'react';
import { useUserSettingsContext } from 'users/utils/contexts/UserSettingsContext';
import { GenerateIcon, ICONS } from '@pileus-cloud/anodot-frontend-common';
import { ResponsiveContainer, Sankey, Tooltip } from 'recharts';
import { NegativeLink, SankeyChartProps, SankeyLinkProps } from './types.ts';
import EmptyStateChart from './EmptystateChart.tsx';
import CustomTooltip from './CustomTooltip';
import styles from './sankeyChart.module.scss';
import CustomLink from './CustomLink.tsx';
import SankeyNode from './SankeyNode.tsx';

const hexToRgba = (hex: string, opacity: number) => {
  const bigint = parseInt(hex.slice(1), 16);
  const r = (bigint >> 16) & 255;
  const g = (bigint >> 8) & 255;
  const b = bigint & 255;
  return `rgba(${r}, ${g}, ${b}, ${opacity})`;
};

const SankeyChart: React.FC<SankeyChartProps> = ({ data, mainNode, overrideCurrency }) => {
  const { getCurrencyNumber } = useUserSettingsContext();
  const [nodePosition, setNodePosition] = useState<{ x: number; y: number; height: number } | null>(null);

  const processedData = useMemo(() => {
    if (!data?.nodes?.length) {
      return {
        nodes: [],
        links: [],
      };
    }
    const targetTotals = new Map<number, number>();

    const targetRendered = new Set<number>();
    const negativeLinksByNode = new Map<number, NegativeLink[]>();

    data.links.forEach((link) => {
      targetTotals.set(link.target, (targetTotals.get(link.target) ?? 0) + link.value);

      if (link.value <= 0) {
        if (link.value <= 0) {
          if (!negativeLinksByNode.has(link.target)) {
            negativeLinksByNode.set(link.target, []);
          }
          negativeLinksByNode.get(link.target)!.push(link);
        }
      }
    });

    const linksWithLabels = data.links.map((link) => {
      const shouldRenderLabel = !targetRendered.has(link.target);
      if (shouldRenderLabel) {
        targetRendered.add(link.target);
      }
      return {
        source: link.source,
        target: link.target,
        value: link.value,
        shouldRenderLabel,
        totalTargetValue: targetTotals.get(link.target) ?? 0,
      };
    });

    const nodesWithNegativeLinks = data.nodes.map((node, index) => ({
      ...node,
      negativeLinks: negativeLinksByNode.get(index) ?? [],
    }));

    return {
      links: linksWithLabels.filter((link) => link.value > 0),
      nodes: nodesWithNegativeLinks,
    };
  }, [data]);

  useEffect(() => {
    if (!nodePosition) {
      return;
    }
    const totalWrapper = document.querySelector(`.${styles.totalWrapper}`) as HTMLDivElement;
    if (totalWrapper) {
      totalWrapper.style.top = `${nodePosition.y - 25}px`;
      totalWrapper.style.left = `${nodePosition.x}px`;
      totalWrapper.style.height = `${nodePosition.height}px`;
    }
  }, [nodePosition]);

  if (!data?.links?.length) {
    return <EmptyStateChart />;
  }
  return (
    <div className={styles.chartWrapper}>
      {nodePosition && mainNode && !isNaN(nodePosition?.y) && (
        <div
          className={styles.totalWrapper}
          style={{
            position: 'relative',
            top: nodePosition.y - 25,
            left: nodePosition.x,
            height: nodePosition.height,
          }}
        >
          <GenerateIcon iconName={ICONS.coins.name} className={styles.icon} />
          <span className={styles.header}>{mainNode.label}</span>
          <span className={styles.value}>{getCurrencyNumber(mainNode.value, null, {}, overrideCurrency)}</span>
        </div>
      )}
      <ResponsiveContainer width="100%" height={300} className={styles.chart}>
        <Sankey
          data={processedData}
          nodePadding={20}
          nodeWidth={15}
          iterations={0}
          node={(nodeProps) => {
            const currentNode = data?.nodes[nodeProps.index] ?? { color: '' };
            const isFirstNode = nodeProps.index === 0;

            return (
              <SankeyNode
                nodeProps={nodeProps}
                isFirstNode={isFirstNode}
                nodePosition={nodePosition}
                setNodePosition={setNodePosition}
                currentNode={currentNode}
              />
            );
          }}
          link={(props: SankeyLinkProps) => {
            const link = processedData.links[props.index];
            const sourceNode = processedData.nodes[link.source];
            const targetNode = processedData.nodes[link.target];
            const gradientId = `gradient-${link.source}-${link.target}`;

            return (
              <g>
                <defs>
                  <linearGradient id={gradientId} x1="0%" y1="0%" x2="100%" y2="0%">
                    <stop offset="0%" stopColor={hexToRgba(sourceNode.color, 0.4)} />
                    <stop offset="100%" stopColor={hexToRgba(targetNode.color, 0.4)} />
                  </linearGradient>
                </defs>
                <CustomLink
                  sourceX={props.sourceX}
                  sourceY={props.sourceY}
                  sourceControlX={props.sourceControlX}
                  targetX={props.targetX}
                  targetY={props.targetY}
                  targetControlX={props.targetControlX}
                  linkWidth={Math.max(props.linkWidth, 1)}
                  sourceColor={`url(#${gradientId})`}
                  targetName={targetNode.name}
                  targetValue={getCurrencyNumber(link.value, null, {}, overrideCurrency)}
                  totalTargetValue={link.totalTargetValue}
                  shouldRenderLabel={link.shouldRenderLabel}
                  totalCost={mainNode.value}
                  overrideCurrency={overrideCurrency}
                />
              </g>
            );
          }}
        >
          <Tooltip content={<CustomTooltip overrideCurrency={overrideCurrency} fullData={processedData} />} />
        </Sankey>
      </ResponsiveContainer>
    </div>
  );
};
export default SankeyChart;
